import { isIdentifierRecalled, type IdentifierRecord, type SectorId } from '@myplayer/schema';
import { useMemo, useState, type ReactElement } from 'react';
import type { WorkspaceOption } from './auth-context.js';
import {
  Button,
  ProductsSearchFilterBar,
  Spinner,
  Tag,
  computeVirtualizedSlice,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildProductContextMenuItems } from './context-menu-items.js';
import { StatusBadge } from './product-detail-panel.js';
import {
  type DuplicateIdentifierConflict,
  resolveIdentifierDisplayStatus,
  resolveIdentifierLifecycleState,
} from './products-firestore.js';
import {
  computeProductFacetCounts,
  formatLastChangedDate,
  resolvePlainProductStatus,
  resolveRecordLastChangedIso,
  resolveRowDestinationLabel,
  type ProductFacetCounts,
  type ProductSortOption,
} from './products-helpers.js';
import { type ProductStatusFilter, type ProductValidationFilter } from './products-table.js';
import { ADMIN_STRINGS } from './strings.js';

export interface ProductListCardProps {
  readonly filteredRecords: readonly IdentifierRecord[];
  readonly selectedIdentifierKey: string | undefined;
  readonly searchQuery: string;
  readonly setSearchQuery: (q: string) => void;
  readonly brandFilter?: string | undefined;
  readonly setBrandFilter?: ((brand: string) => void) | undefined;
  readonly statusFilter?: ProductStatusFilter;
  readonly setStatusFilter?: (status: ProductStatusFilter) => void;
  readonly sectorFilter?: 'ALL' | SectorId;
  readonly setSectorFilter?: (sector: 'ALL' | SectorId) => void;
  readonly validationFilter?: ProductValidationFilter;
  readonly setValidationFilter?: (val: ProductValidationFilter) => void;
  readonly workspaceFilter?: string | undefined;
  readonly setWorkspaceFilter?: ((wsId: string) => void) | undefined;
  readonly availableWorkspaces?: readonly WorkspaceOption[] | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
  readonly showWorkspaceBadges?: boolean | undefined;
  readonly sortBy?: ProductSortOption | undefined;
  readonly setSortBy?: ((sort: ProductSortOption) => void) | undefined;
  readonly viewMode?: 'table' | 'cards' | undefined;
  readonly setViewMode?: ((mode: 'table' | 'cards') => void) | undefined;
  readonly facetCounts?: ProductFacetCounts | undefined;
  readonly onSelectRecord: (identifierKey: string) => void;
  readonly onEditRecord?: ((identifierKey: string) => void) | undefined;
  readonly effectiveResolverHost?: string | undefined;
  readonly canEdit?: boolean | undefined;
  readonly showSearchBar?: boolean;
  readonly loading?: boolean;
  readonly duplicateConflicts?: ReadonlyMap<string, DuplicateIdentifierConflict> | undefined;
}

const PRODUCT_CARD_ROW_HEIGHT = 76;

export function ProductListCard({
  filteredRecords,
  selectedIdentifierKey,
  searchQuery,
  setSearchQuery,
  brandFilter = 'ALL',
  setBrandFilter,
  statusFilter = 'ALL',
  setStatusFilter,
  sectorFilter = 'ALL',
  setSectorFilter,
  validationFilter = 'ALL',
  setValidationFilter,
  workspaceFilter = 'ALL',
  setWorkspaceFilter,
  availableWorkspaces,
  workspaceNameMap,
  showWorkspaceBadges = false,
  sortBy = 'MODIFIED_DESC',
  setSortBy,
  viewMode = 'cards',
  setViewMode,
  facetCounts: propFacetCounts,
  onSelectRecord,
  onEditRecord,
  effectiveResolverHost,
  canEdit,
  showSearchBar = false,
  loading = false,
  duplicateConflicts,
}: ProductListCardProps): ReactElement {
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(640);
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const facetCounts = useMemo(
    () => propFacetCounts ?? computeProductFacetCounts(filteredRecords),
    [propFacetCounts, filteredRecords],
  );
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    brandFilter !== 'ALL' ||
    statusFilter !== 'ALL' ||
    sectorFilter !== 'ALL' ||
    validationFilter !== 'ALL' ||
    workspaceFilter !== 'ALL';
  const isSearchVisible = showSearchBar || hasActiveFilters;

  const virtualSlice = computeVirtualizedSlice({
    items: filteredRecords,
    scrollTop,
    viewportHeight,
    rowHeight: PRODUCT_CARD_ROW_HEIGHT,
  });

  return (
    <div className="min-w-0 space-y-3" data-testid="products-cards-view">
      {isSearchVisible && (
        <ProductsSearchFilterBar
          idPrefix="filter-card-product"
          ariaLabel="Product card search bar"
          clearSearchAriaLabel="Clear search query"
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          brandFilter={brandFilter}
          setBrandFilter={setBrandFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          sectorFilter={sectorFilter}
          setSectorFilter={setSectorFilter}
          validationFilter={validationFilter}
          setValidationFilter={setValidationFilter}
          workspaceFilter={workspaceFilter}
          setWorkspaceFilter={setWorkspaceFilter}
          availableWorkspaces={availableWorkspaces}
          sortBy={sortBy}
          onSortChange={setSortBy}
          viewMode={viewMode}
          setViewMode={setViewMode}
          facetCounts={facetCounts}
          onFilterChangeResetScroll={() => setScrollTop(0)}
        />
      )}

      <div className="space-y-2.5">
        <div
          data-testid="products-cards-virtual-scroll-container"
          onScroll={(e) => {
            const target = e.currentTarget;
            setScrollTop(target.scrollTop);
            if (target.clientHeight > 0) {
              setViewportHeight(target.clientHeight);
            }
          }}
          className="max-h-[calc(100vh-220px)] overflow-y-auto"
        >
          {loading ? (
            <div
              role="status"
              data-testid="products-loading-state"
              className="flex items-center justify-center gap-2.5 rounded-card border border-border bg-surface py-10 text-[13px] text-fg-secondary shadow-card-base"
            >
              <Spinner size={18} />
              <span>{ADMIN_STRINGS.loading.products}</span>
            </div>
          ) : filteredRecords.length === 0 ? (
            <p
              data-testid="products-empty-state"
              className="rounded-card border border-border bg-surface py-10 text-center text-[13px] text-fg-secondary shadow-card-base"
            >
              {ADMIN_STRINGS.empty.productsCard}
            </p>
          ) : (
            <ul
              aria-label="Identifiers list"
              className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {virtualSlice.topSpacerHeight > 0 && (
                <li
                  aria-hidden="true"
                  className="col-span-full"
                  style={{ height: `${virtualSlice.topSpacerHeight}px` }}
                />
              )}
              {virtualSlice.visibleItems.map((rec) => {
                const duplicateConflict = duplicateConflicts?.get(
                  `${rec.orgId ?? ''}::${rec.workspaceId ?? ''}::${rec.identifierKey}`,
                );
                const status = duplicateConflict ? 'invalid' : resolveIdentifierDisplayStatus(rec);
                const lifecycleState = resolveIdentifierLifecycleState(rec);
                const plainStatus = resolvePlainProductStatus(rec);
                const isSelected = rec.identifierKey === selectedIdentifierKey;
                const displayTitle = rec.draft?.title ?? rec.title ?? 'Untitled Identifier';
                const brand = rec.draft?.brand ?? rec.brand;
                const destination = resolveRowDestinationLabel(rec);
                const lastChanged = formatLastChangedDate(resolveRecordLastChangedIso(rec));
                const wsName = rec.workspaceId
                  ? (workspaceNameMap?.get(rec.workspaceId) ?? rec.workspaceId)
                  : undefined;
                return (
                  <li key={`${rec.workspaceId ?? 'ws'}_${rec.identifierKey}`}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="inline"
                      aria-pressed={isSelected}
                      data-testid={`identifier-row-${rec.identifierKey}`}
                      onClick={() => onSelectRecord(rec.identifierKey)}
                      onContextMenu={bindContextMenu(() =>
                        buildProductContextMenuItems({
                          record: rec,
                          effectiveResolverHost,
                          canEdit,
                          onSelectRecord,
                          onEditRecord,
                        }),
                      )}
                      className={`flex h-full w-full flex-col items-stretch justify-between gap-2.5 rounded-card border p-4 text-left shadow-card-base transition-colors ${
                        isSelected
                          ? 'border-brand bg-brand-wash text-fg'
                          : 'border-border bg-surface hover:border-border-medium hover:bg-surface-subtle'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <p className="truncate text-[14px] font-semibold text-fg">
                              {displayTitle}
                            </p>
                            {showWorkspaceBadges && wsName && (
                              <Tag
                                variant="outline"
                                size="sm"
                                data-testid={`product-ws-badge-${rec.identifierKey}`}
                              >
                                {wsName}
                              </Tag>
                            )}
                          </div>
                          <p className="mt-0.5 truncate font-mono text-[12px] text-fg-secondary tabular-nums">
                            {brand ? `${brand} · ` : ''}AI ({rec.ai}) {rec.primaryKey}
                          </p>
                          {duplicateConflict !== undefined && (
                            <p
                              data-testid={`card-duplicate-conflict-${rec.identifierKey}`}
                              className="mt-0.5 truncate text-[11px] font-medium text-status-error"
                            >
                              {duplicateConflict.message}
                            </p>
                          )}
                        </div>
                        <StatusBadge
                          status={status}
                          lifecycleState={lifecycleState}
                          plainStatus={plainStatus}
                          recalled={isIdentifierRecalled(rec)}
                        />
                      </div>
                      <div className="flex items-center justify-between gap-2 border-t border-subtle pt-2.5 text-[12px] text-fg-secondary tabular-nums">
                        <span className="min-w-0 flex-1 truncate font-mono">{destination}</span>
                        <span className="shrink-0">{lastChanged}</span>
                      </div>
                    </Button>
                  </li>
                );
              })}
              {virtualSlice.bottomSpacerHeight > 0 && (
                <li
                  aria-hidden="true"
                  className="col-span-full"
                  style={{ height: `${virtualSlice.bottomSpacerHeight}px` }}
                />
              )}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between rounded-card border border-border bg-surface px-4 py-2.5 text-[12px] text-fg-secondary shadow-card-base tabular-nums">
          <span data-testid="products-cards-pagination-summary">
            Showing {virtualSlice.visibleItems.length} of {filteredRecords.length} products
          </span>
        </div>
      </div>
      {contextMenuElement}
    </div>
  );
}
